// One button per change (SPEC §7.4): cancel/restore, room, time, notes, back to main teacher.
// Rendered for admins only; viewers see no action buttons at all (SPEC §7, "Read-only means").

import {
  cancelLesson, changeRoom, moveTime, resetLessonPart, restoreLesson, setLessonNote, setTeacherNote,
} from "../../api/lessons";
import type { Classroom, Lesson } from "../../api/types";
import { t } from "../../i18n/i18n";
import { createButton, createElement } from "../../ui/dom";
import { createSelectField, createTextAreaField, type Field } from "../../ui/forms";
import { createTimeRangePicker, type TimeRangePicker } from "../../ui/timeRangePicker";
import type { LessonAction, LessonPageState } from "./lessonState";

export interface ActionHandlers {
  perform: (action: LessonAction) => Promise<void>;
  toggleEditor: (editor: LessonPageState["openEditor"]) => void;
}

/** The action buttons and the open inline editor, if any. */
export function buildLessonActions(state: LessonPageState, handlers: ActionHandlers): HTMLElement {
  const wrapper: HTMLDivElement = createElement("div");
  const buttons: HTMLDivElement = createElement("div", "lesson-actions");
  const lesson: Lesson = state.lesson;
  if (lesson.isCenterClosed) {
    buttons.append(roomButton(lesson, handlers));
  } else {
    buttons.append(...allButtons(lesson, handlers));
  }

  wrapper.append(buttons);
  const editor: HTMLElement | null = buildOpenEditor(state, handlers);
  if (editor !== null) {
    wrapper.append(editor);
  }

  return wrapper;
}

function allButtons(lesson: Lesson, handlers: ActionHandlers): HTMLElement[] {
  const version: number = lesson.changeVersion;
  const buttons: HTMLElement[] = [lesson.isCancelled
    ? createButton(t("lesson.restore"), (): Promise<void> => handlers.perform(
      (confirm: boolean) => restoreLesson(lesson.key, version, confirm)))
    : createButton(t("lesson.cancel"), (): Promise<void> => handlers.perform(
      () => cancelLesson(lesson.key, version)), "button-danger")];
  buttons.push(roomButton(lesson, handlers), timeButton(lesson, handlers));
  buttons.push(createButton(t("lesson.editNote"), (): void => {
    handlers.toggleEditor("note");
  }), createButton(t("lesson.editTeacherNote"), (): void => {
    handlers.toggleEditor("teacherNote");
  }));
  if (lesson.isSubstituted) {
    buttons.push(createButton(t("lesson.backToMainTeacher"), (): Promise<void> => handlers.perform(
      (confirm: boolean) => resetLessonPart(lesson.key, "substitute", version, confirm))));
  }

  return buttons;
}

function roomButton(lesson: Lesson, handlers: ActionHandlers): HTMLElement {
  if (lesson.isRoomChanged && lesson.isCenterClosed === false) {
    return createButton(t("lesson.resetRoom"), (): Promise<void> => handlers.perform(
      (confirm: boolean) => resetLessonPart(lesson.key, "room", lesson.changeVersion, confirm)));
  }

  return createButton(t("lesson.changeRoom"), (): void => {
    handlers.toggleEditor("room");
  });
}

function timeButton(lesson: Lesson, handlers: ActionHandlers): HTMLElement {
  if (lesson.isMoved) {
    return createButton(t("lesson.resetTime"), (): Promise<void> => handlers.perform(
      (confirm: boolean) => resetLessonPart(lesson.key, "time", lesson.changeVersion, confirm)));
  }

  return createButton(t("lesson.moveTime"), (): void => {
    handlers.toggleEditor("time");
  });
}

function buildOpenEditor(state: LessonPageState, handlers: ActionHandlers): HTMLElement | null {
  switch (state.openEditor) {
    case "room":
      return buildRoomEditor(state, handlers);
    case "time":
      return buildTimeEditor(state.lesson, handlers);
    case "note":
      return buildNoteEditor(state.lesson, handlers, false);
    case "teacherNote":
      return buildNoteEditor(state.lesson, handlers, true);
    default:
      return null;
  }
}

function buildRoomEditor(state: LessonPageState, handlers: ActionHandlers): HTMLElement {
  const options: { value: string; label: string }[] = state.classrooms
    .filter((classroom: Classroom): boolean => classroom.archived === false)
    .map((classroom: Classroom): { value: string; label: string } => ({ value: classroom.id, label: classroom.name }));
  const field: Field<HTMLSelectElement> = createSelectField(t("lesson.room"), options, state.lesson.classroomId);
  const lesson: Lesson = state.lesson;
  return buildEditor(field.wrapper, (): Promise<void> => handlers.perform(
    (confirm: boolean) => changeRoom(lesson.key, field.input.value, lesson.changeVersion, confirm)), handlers);
}

/** Start and end pickers on the same date; the end follows the start keeping the length (SPEC §7.4). */
function buildTimeEditor(lesson: Lesson, handlers: ActionHandlers): HTMLElement {
  const picker: TimeRangePicker = createTimeRangePicker(lesson.range);
  return buildEditor(picker.element, (): Promise<void> => handlers.perform(
    (confirm: boolean) => moveTime(lesson.key, picker.getRange(), lesson.changeVersion, confirm)), handlers);
}

function buildNoteEditor(lesson: Lesson, handlers: ActionHandlers, isForTeacher: boolean): HTMLElement {
  const label: string = isForTeacher ? t("lesson.teacherNote") : t("lesson.note");
  const field: Field<HTMLTextAreaElement> = createTextAreaField(label, (isForTeacher ? lesson.teacherNote
    : lesson.note) ?? "");
  const save = (): Promise<void> => handlers.perform(() => isForTeacher
    ? setTeacherNote(lesson.key, field.input.value, lesson.changeVersion)
    : setLessonNote(lesson.key, field.input.value, lesson.changeVersion));
  return buildEditor(field.wrapper, save, handlers);
}

function buildEditor(content: HTMLElement, onSave: () => Promise<void>, handlers: ActionHandlers): HTMLElement {
  const editor: HTMLDivElement = createElement("div", "inline-editor");
  editor.append(content, createButton(t("app.save"), onSave, "button-primary"), createButton(t("app.cancel"),
    (): void => {
      handlers.toggleEditor(null);
    }));
  return editor;
}
